// @ts-strict-ignore
import React, {
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { AggregationEditorContext } from './context';
import {
  buildOptionGroups,
  getColumnKey,
  resolveDataType,
  sortAggregationFilterOptionsByLabel,
  toFilterOptions,
} from './helpers';
import type { AggregationFilter } from 'flow/store/engine/types';
import type { AggregationFilterOption } from '~/dashboard/views/settings/aggregations/types';
import { AggregationFilterOperatorType } from 'flow/store/engine/constants';
import AggregationFilterRow from './AggregationFilterRow';
import AggregationSearchableDropdown from './AggregationSearchableDropdown';

const AggregationFiltersSection = () => {
  const {
    sourceData, setSourceData, aggregationConfig,
  } = useContext(AggregationEditorContext);

  const filterOptions = useMemo(
    () => toFilterOptions(aggregationConfig.columns),
    [aggregationConfig.columns],
  );
  const filterOptionsGrouped = useMemo(
    () => buildOptionGroups({
      options: sortAggregationFilterOptionsByLabel(filterOptions),
      objectDisplayName: aggregationConfig.objectDisplayName,
    }),
    [filterOptions, aggregationConfig.objectDisplayName],
  );

  const handleFilterToggle = useCallback((option: AggregationFilterOption, checked: boolean) => {
    if (checked) {
      const dataType = resolveDataType(option);
      const isNumeric = dataType === 'integer' || dataType === 'decimal';
      const newFilter: AggregationFilter = {
        columnSlug: option.column_slug ?? option.value,
        operatorSlug: AggregationFilterOperatorType.EQ,
        staticValue: isNumeric ? '0' : '',
        dynamicColumnSlug: null,
        ...(option.ui_data_type === 'published_attribute' && {
          publishedAttributeMetadata: {
            publishedAttributeToken: option.value,
            publishedAttributeDescription: option.published_attribute_description ?? null,
            publishedAttributeName: option.published_attribute_name,
            publishedAttributeDataType: option.data_type,
            containsPii: option.contains_pii,
          },
        }),
      };
      setSourceData(prev => ({
        ...prev,
        filters: [...prev.filters, newFilter],
      }));
    } else {
      setSourceData(prev => ({
        ...prev,
        filters: prev.filters.filter(f => getColumnKey(f) !== option.value),
      }));
    }
  }, [setSourceData]);

  const handleUpdateFilter = useCallback((
    filterKey: string,
    updates: Partial<AggregationFilter>,
  ) => {
    setSourceData(prev => ({
      ...prev,
      filters: prev.filters.map(f => (
        getColumnKey(f) === filterKey ? { ...f, ...updates } : f
      )),
    }));
  }, [setSourceData]);

  const handleRemoveFilter = useCallback((filterKey: string) => {
    setSourceData(prev => ({
      ...prev,
      filters: prev.filters.filter(f => getColumnKey(f) !== filterKey),
    }));
  }, [setSourceData]);

  const selectedFilterValues = useMemo(
    () => sourceData.filters.map(f => getColumnKey(f)),
    [sourceData.filters],
  );

  if (filterOptions.length === 0) return null;

  return (
    <>
      {sourceData.filters.map(filter => {
        const key = getColumnKey(filter);
        const option = filterOptions.find(o => o.value === key);
        return (
          <AggregationFilterRow
            key={key}
            filter={filter}
            filterOption={option}
            onUpdate={handleUpdateFilter}
            onRemove={handleRemoveFilter}
          />
        );
      })}

      <AggregationSearchableDropdown
        mode="multi"
        groups={filterOptionsGrouped}
        selectedValues={selectedFilterValues}
        placeholder="Add/Remove Filter"
        searchPlaceholder="Search"
        emptyMessage="No filters available."
        onToggle={(option, checked) => {
          const filterOption = filterOptions.find(o => o.value === option.value);
          if (filterOption) handleFilterToggle(filterOption, checked);
        }}
      />
    </>
  );
};

export default AggregationFiltersSection;
